import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ComponentRef,
  inject,
  Input,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { DynamicImportLoaderService } from '@base/services/private/dynamic-import.service';
import { AgendaService } from '@base/services/private/agenda.service';
import { Speaker } from '@base/models/responses/speaker.res';
import { ModalSpeakerComponent } from '@base/components/modal-speaker/modal-speaker.component';
import { WhiteLabel } from '@config/whitelabel-keys.constant';
import { Sessions } from '@base/models/responses/sessions-of-company.res';
import { SearchReason } from '@base/models/others/match-reason';
import { GlobalService } from '@base/services/private/global.service';

@Component({
  selector: 'app-speaker-detail',
  templateUrl: './speaker-detail.component.html',
  styleUrl: './speaker-detail.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SpeakerDetailComponent implements AfterViewInit {
  @ViewChild('speakerDetailContainer', { read: ViewContainerRef }) speakerDetailContainer!: ViewContainerRef;
  @Input() speakerId: string | null = null;
  @Input() searchReasons: SearchReason[] | null = null;
  private readonly dynamicImportLoaderSrv = inject(DynamicImportLoaderService);
  private readonly agendaSrv = inject(AgendaService);
  private readonly globalSrv = inject(GlobalService);
  speakerDetailInstance: ComponentRef<unknown> | undefined;
  speaker: Speaker | null = null;
  sessions: Sessions[] = [];

  async ngAfterViewInit(): Promise<void> {
    await this.loadSpeakerDetailComponent();
    this.getSpeakerDetail();
  }

  /**
   * Fetches the details of the speaker.
   * @returns void
   */
  getSpeakerDetail(): void {
    if (!this.speakerId) return;

    this.agendaSrv.getSpeakerDetail(this.speakerId).subscribe({
      next: (res) => {
        if (!res) {
          this.globalSrv.destroyModalRef('modal-speaker');
          return;
        }
        // Detail responses normally omit search reasons. The clicked list item passes them in.
        this.speaker = {
          ...res.speaker,
          search_reasons: this.searchReasons ?? res.speaker.search_reasons,
        };
        this.sessions = res.sessions;
        this.renderSpeakerDetail();
      },
      error: (err) => {
        console.error('Error fetching speaker detail:', err);
      },
    });
  }

  /**
   * Dynamically loads the speaker detail component into the view.
   */
  async loadSpeakerDetailComponent(): Promise<void> {
    try {
      const component = await this.dynamicImportLoaderSrv.loadComponent(
        this.speakerDetailContainer,
        WhiteLabel.COMPONENTS.ModalSpeaker // Replace with actual path
      );
      if (component) {
        this.speakerDetailInstance = component.componentRef || undefined;
      } else {
        throw new Error('Components could not be loaded.');
      }
    } catch (error) {
      console.error('Failed to load components:', error);
    }
  }

  /**
   * Renders speaker details in the modal component.
   */
  renderSpeakerDetail(): void {
    const component = this.speakerDetailInstance?.instance as ModalSpeakerComponent; // Replace with actual component type
    if (component) {
      this.speakerDetailInstance?.setInput('speaker', this.speaker);
      this.speakerDetailInstance?.setInput('sessions', this.sessions);
      this.speakerDetailInstance?.setInput('modalId', 'modal-speaker');
    }
  }
}
